import React from 'react';
import { RenewalStatus } from '../models/ResourceModels';
import { forceRefresh, PollerMode, subscribe } from '../service/renewalPollerRegistry';
import { EXPAND_DEBOUNCE_MS } from '../../constants/RenewalPolling';

export interface RenewalStatusPolling {
  status: RenewalStatus | undefined;
  isPollingStopped: boolean;
  refresh: () => void;
}

// Thin wrapper around renewalPollerRegistry: owns nothing but the debounce (a
// per-subscriber concern) and re-rendering this component when the shared entry changes.
export function useRenewalStatusPolling(accountId: string, mode: PollerMode, enabled: boolean): RenewalStatusPolling {
  const [status, setStatus] = React.useState<RenewalStatus | undefined>(undefined);
  const [isPollingStopped, setIsPollingStopped] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) {
      setStatus(undefined);
      setIsPollingStopped(false);
      return;
    }

    let unsubscribe: (() => void) | undefined;
    // Debounce presence-mode subscriptions only: a partner scanning/scrolling through a
    // list of rows shouldn't fire a GET per row per click. Detail mode (modal explicitly
    // opened by a click) subscribes immediately.
    const delay = mode === 'presence' ? EXPAND_DEBOUNCE_MS : 0;
    const timeoutId = setTimeout(() => {
      unsubscribe = subscribe(accountId, mode, (nextStatus, nextIsPollingStopped) => {
        setStatus(nextStatus);
        setIsPollingStopped(nextIsPollingStopped);
      });
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      unsubscribe?.();
    };
  }, [accountId, mode, enabled]);

  const refresh = React.useCallback(() => {
    forceRefresh(accountId);
  }, [accountId]);

  return { status, isPollingStopped, refresh };
}
